import { useState, useRef, useCallback, useEffect } from 'react';
import { SearchIndexerService } from '../services/SearchIndexerService';
import { coreSettingsService } from '../services/CoreSettingsService';
import { trackFeatureUsage, FeatureEvent, EventProp } from '../observability';
import type {
    InjioRegistrySource,
    SearchChunkResult,
    SourceItem,
    ListColumn,
    SourceRegistrationRequest,
    IndexRunSummary,
} from '@shared/dto/searchIndexer';
import {
    SEARCH_INDEX_LEASE_HEARTBEAT_MS,
    SEARCH_INDEX_REGEN_DAYS,
} from '../config/constants';

export interface SourceRunProgress {
    sourceListId: string;
    displayName: string;
    status: 'pending' | 'running' | 'completed' | 'error' | 'busy';
    total: number;
    processed: number;
    newlyIndexed: number;
    cacheHits: number;
    regenerated: number;
    unpublishedSkipped: number;
    noContentSkipped: number;
    chunkWriteFailures: number;
    errorMessage?: string;
}

export type SearchIndexerContextValue = ReturnType<typeof useSearchIndexer>;

export function useSearchIndexer(siteUrl: string) {
    const serviceRef = useRef<SearchIndexerService | null>(null);
    const workerRef = useRef<Worker | null>(null);
    const workerReadyRef = useRef(false);
    const modelIdRef = useRef('');
    const isRunningRef = useRef(false);
    const sourcesRef = useRef<InjioRegistrySource[]>([]);

    const [registryExists, setRegistryExists] = useState<boolean | null>(null);
    const [registryUrl, setRegistryUrl] = useState<string | null>(null);
    const [sources, setSources] = useState<InjioRegistrySource[]>([]);
    const [sourcesLoading, setSourcesLoading] = useState(false);
    const [workerReady, setWorkerReady] = useState(false);
    const [workerLoading, setWorkerLoading] = useState(false);
    const [workerStatus, setWorkerStatus] = useState('');
    const [workerError, setWorkerError] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [runProgress, setRunProgress] = useState<SourceRunProgress[]>([]);
    const [runSummaries, setRunSummaries] = useState<IndexRunSummary[]>([]);
    const [cacheStats, setCacheStats] = useState<Record<string, number>>({});
    const [cacheStatsLoading, setCacheStatsLoading] = useState(false);
    const [isProvisioning, setIsProvisioning] = useState(false);
    const [provisionStatus, setProvisionStatus] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Lazy service singleton
    if (!serviceRef.current) {
        serviceRef.current = new SearchIndexerService();
    }

    // Init worker once siteUrl is known — waits on the configured model ID from
    // CoreSettingsService first (same source Dashboard.tsx uses for the Copilot
    // pipeline's worker) so the embedding model actually follows Admin Settings
    // instead of always falling back to the worker's own hardcoded default.
    useEffect(() => {
        if (!siteUrl) return;
        let cancelled = false;

        setWorkerLoading(true);
        setWorkerStatus('Initializing search indexer engine...');

        // IMPORTANT: new URL() must be inline inside new Worker() for Vite's
        // static analysis to detect and bundle the worker as a separate .js asset.
        const w = new Worker(
            new URL('../workers/SearchIndexerWorker.ts', import.meta.url),
            { type: 'module' },
        );
        workerRef.current = w;

        w.onmessage = (e: MessageEvent) => {
            const { type, message, error: workerErrMsg, modelId } = e.data;
            if (type === 'STATUS') {
                setWorkerStatus(message ?? '');
            } else if (type === 'init-ok') {
                workerReadyRef.current = true;
                modelIdRef.current = modelId ?? '';
                setWorkerReady(true);
                setWorkerLoading(false);
                setWorkerStatus('Search indexer ready');
            } else if (type === 'init-error') {
                setWorkerError(workerErrMsg ?? 'Worker initialization failed');
                setWorkerLoading(false);
            }
            // item-chunks / item-error handled by per-item Promise listeners in startFullIndexRun
        };

        w.onerror = (e) => {
            setWorkerError(e.message ?? 'Worker script error');
            setWorkerLoading(false);
        };

        (async () => {
            let modelId: string | undefined;
            try {
                const config = await coreSettingsService.getSettings(siteUrl);
                modelId = config.modelId;
            } catch {
                // Fall back to the worker's own default if settings can't be loaded.
            }
            if (!cancelled) w.postMessage({ type: 'init', modelId });
        })();

        return () => {
            cancelled = true;
            w.terminate();
            workerRef.current = null;
            workerReadyRef.current = false;
        };
    }, [siteUrl]);

    // Reload sources whenever siteUrl changes
    useEffect(() => {
        if (siteUrl) loadSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteUrl]);

    // Restore the last completed run's summary from localStorage so the Index Run
    // tab doesn't go blank on a page refresh — this is purely a client-side
    // convenience cache; nothing here is persisted server-side beyond each
    // source's own LastFullSyncDate.
    useEffect(() => {
        if (!siteUrl) return;
        const persisted = _loadPersistedRun(siteUrl);
        setRunProgress(persisted?.runProgress ?? []);
        setRunSummaries(persisted?.runSummaries ?? []);
    }, [siteUrl]);

    const loadSources = useCallback(async () => {
        if (!siteUrl) return;
        setSourcesLoading(true);
        setError(null);
        try {
            const { exists, sources: fetched, registryUrl: url } = await serviceRef.current!.checkRegistryExists(siteUrl);
            setRegistryExists(exists);
            setRegistryUrl(url);
            setSources(fetched);
            sourcesRef.current = fetched;
        } catch (err: any) {
            setError(err.message ?? 'Failed to load sources');
        } finally {
            setSourcesLoading(false);
        }
    }, [siteUrl]);

    const registerSource = useCallback(
        async (config: SourceRegistrationRequest): Promise<InjioRegistrySource> => {
            const newSource = await serviceRef.current!.registerAnnouncementSource(siteUrl, config);
            await loadSources();
            return newSource;
        },
        [siteUrl, loadSources],
    );

    const clearSourceCache = useCallback(
        async (source: InjioRegistrySource): Promise<void> => {
            const rows = await serviceRef.current!.fetchCacheItems(siteUrl, source);
            const uniqueIds = [...new Set(rows.map(r => r.itemUniqueId))];
            for (const uid of uniqueIds) {
                await serviceRef.current!.removeCacheRowsForItem(siteUrl, source, uid);
            }
            setCacheStats(prev => ({ ...prev, [source.listId]: 0 }));
        },
        [siteUrl],
    );

    const refreshCacheStats = useCallback(async (): Promise<void> => {
        const currentSources = sourcesRef.current;
        if (!currentSources.length) return;
        setCacheStatsLoading(true);
        try {
            const stats: Record<string, number> = {};
            for (const source of currentSources) {
                const rows = await serviceRef.current!.fetchCacheItems(siteUrl, source);
                stats[source.listId] = rows.length;
            }
            setCacheStats(stats);
        } finally {
            setCacheStatsLoading(false);
        }
    }, [siteUrl]);

    const provisionRegistry = useCallback(async (): Promise<void> => {
        setIsProvisioning(true);
        setProvisionStatus('Starting…');
        setError(null);
        try {
            await serviceRef.current!.provisionRegistry(siteUrl, msg => setProvisionStatus(msg));
            await loadSources();
        } catch (err: any) {
            setError(err.message ?? 'Registry setup failed');
            throw err;
        } finally {
            setIsProvisioning(false);
        }
    }, [siteUrl, loadSources]);

    const getSiteLists = useCallback(
        () => serviceRef.current!.getSiteLists(siteUrl),
        [siteUrl],
    );

    const getSourceColumns = useCallback(
        (listId: string): Promise<ListColumn[]> =>
            serviceRef.current!.getSiteListColumns(siteUrl, listId),
        [siteUrl],
    );

    const deleteSource = useCallback(
        async (source: InjioRegistrySource): Promise<void> => {
            await serviceRef.current!.deleteRegistrySource(siteUrl, source);
            await loadSources();
        },
        [siteUrl, loadSources],
    );

    const updateSourceTagField = useCallback(
        async (source: InjioRegistrySource, fieldName: string): Promise<void> => {
            await serviceRef.current!.updateTagFieldName(siteUrl, source, fieldName);
            await loadSources();
        },
        [siteUrl, loadSources],
    );

    const startFullIndexRun = useCallback(async (): Promise<void> => {
        if (!workerReadyRef.current || isRunningRef.current) return;
        const currentSources = sourcesRef.current;
        if (!currentSources.length) return;

        isRunningRef.current = true;
        setIsRunning(true);
        setRunSummaries([]);
        _clearPersistedRun(siteUrl);
        trackFeatureUsage(FeatureEvent.SearchIndexerRunStarted, { [EventProp.SourceCount]: String(currentSources.length) });

        setRunProgress(
            currentSources.map(s => ({
                sourceListId: s.listId,
                displayName: s.displayName ?? s.listId,
                status: 'pending',
                total: 0,
                processed: 0,
                newlyIndexed: 0,
                cacheHits: 0,
                regenerated: 0,
                unpublishedSkipped: 0,
                noContentSkipped: 0,
                chunkWriteFailures: 0,
            })),
        );

        const summaries: IndexRunSummary[] = [];

        for (let source of currentSources) {
            setRunProgress(prev =>
                prev.map(p => (p.sourceListId === source.listId ? { ...p, status: 'running' } : p)),
            );

            let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

            try {
                const leaseResult = await serviceRef.current!.acquireLease(siteUrl, source);
                if (!leaseResult.acquired) {
                    summaries.push({
                        source, status: 'busy', total: 0, newlyIndexed: 0,
                        regenerated: 0, cacheHits: 0, unpublishedSkipped: 0,
                        noContentSkipped: 0, chunkWriteFailures: 0,
                        busyOwner: leaseResult.ownedBy,
                    });
                    setRunProgress(prev =>
                        prev.map(p =>
                            p.sourceListId === source.listId
                                ? { ...p, status: 'busy', errorMessage: `Locked by ${leaseResult.ownedBy}` }
                                : p,
                        ),
                    );
                    continue;
                }

                // Self-heals the cache list's schema every run (not just at
                // registration), and swaps in a fresh cacheListId/Url if it had
                // to be recreated for a schema migration.
                source = await serviceRef.current!.ensureCacheListSchema(siteUrl, source);

                heartbeatTimer = setInterval(async () => {
                    try {
                        await serviceRef.current!.renewLease(siteUrl, source);
                    } catch (e) {
                        console.warn('[useSearchIndexer] Lease renewal failed', e);
                    }
                }, SEARCH_INDEX_LEASE_HEARTBEAT_MS);

                const [items, cacheRows] = await Promise.all([
                    serviceRef.current!.fetchSourceItems(siteUrl, source),
                    serviceRef.current!.fetchCacheItems(siteUrl, source),
                ]);

                // Build itemUniqueId → most-recent cachedDate
                const cacheMap = new Map<string, string>();
                for (const row of cacheRows) {
                    const existing = cacheMap.get(row.itemUniqueId);
                    if (!existing || (row.cachedDate ?? '') > existing) {
                        cacheMap.set(row.itemUniqueId, row.cachedDate ?? '');
                    }
                }

                setRunProgress(prev =>
                    prev.map(p =>
                        p.sourceListId === source.listId ? { ...p, total: items.length } : p,
                    ),
                );

                let newlyIndexed = 0, cacheHits = 0, regenerated = 0, unpublishedSkipped = 0;
                let noContentSkipped = 0, chunkWriteFailures = 0;

                for (let i = 0; i < items.length; i++) {
                    const item = items[i];

                    // Skip pages that have never been published as a major version
                    // (resolved from version history in fetchSourceItems — see
                    // SearchIndexerService._resolvePagePublishState).
                    if (source.sourceType === 'Page' && item.isPublished === false) {
                        unpublishedSkipped++;
                        setRunProgress(prev =>
                            prev.map(p =>
                                p.sourceListId === source.listId ? { ...p, processed: i + 1 } : p,
                            ),
                        );
                        continue;
                    }

                    const existingCachedDate = cacheMap.get(item.itemUniqueId);
                    if (existingCachedDate) {
                        const itemModifiedMs = new Date(item.lastModifiedDateTime).getTime();
                        const indexedMs = new Date(existingCachedDate).getTime();
                        const regenThresholdMs =
                            Date.now() - SEARCH_INDEX_REGEN_DAYS * 86_400_000;

                        if (itemModifiedMs <= indexedMs && indexedMs > regenThresholdMs) {
                            cacheHits++;
                            setRunProgress(prev =>
                                prev.map(p =>
                                    p.sourceListId === source.listId
                                        ? { ...p, processed: i + 1, cacheHits }
                                        : p,
                                ),
                            );
                            continue;
                        }

                        regenerated++;
                        await serviceRef.current!.removeCacheRowsForItem(
                            siteUrl,
                            source,
                            item.itemUniqueId,
                        );
                    } else {
                        newlyIndexed++;
                    }

                    const content = _extractItemContent(item, source.sourceType);

                    const chunks = await _processItemInWorker(
                        workerRef.current!,
                        item.itemUniqueId,
                        content,
                        item.title,
                        source.sourceType,
                        String(item.fields?.FileLeafRef ?? ''),
                        Number(item.fields?.PromotedState ?? 0),
                    );

                    if (chunks.length === 0) {
                        // Worker produced nothing to cache — e.g. a Page below the
                        // low-content threshold, or content that failed to extract.
                        // Distinct from a write failure: nothing was even attempted here.
                        noContentSkipped++;
                    }

                    const tag = await serviceRef.current!.readItemTag(siteUrl, source, item);
                    for (const chunk of chunks) {
                        const wrote = await serviceRef.current!.writeCacheRow(siteUrl, source, item, chunk, tag, modelIdRef.current);
                        if (!wrote) {
                            // writeCacheRow already logged the underlying Graph error —
                            // surface the failure count here so the run doesn't silently
                            // report "completed" while nothing actually landed in the cache list.
                            chunkWriteFailures++;
                        }
                    }

                    setRunProgress(prev =>
                        prev.map(p =>
                            p.sourceListId === source.listId
                                ? { ...p, processed: i + 1, newlyIndexed, regenerated, cacheHits, noContentSkipped, chunkWriteFailures }
                                : p,
                        ),
                    );
                }

                await serviceRef.current!.updateLastSyncDate(siteUrl, source);

                summaries.push({
                    source, status: 'completed', total: items.length,
                    newlyIndexed, regenerated, cacheHits, unpublishedSkipped,
                    noContentSkipped, chunkWriteFailures,
                });
                setRunProgress(prev =>
                    prev.map(p =>
                        p.sourceListId === source.listId
                            ? { ...p, status: 'completed', newlyIndexed, regenerated, cacheHits, unpublishedSkipped, noContentSkipped, chunkWriteFailures }
                            : p,
                    ),
                );
            } catch (err: any) {
                console.error(`[useSearchIndexer] Failed source ${source.listId}`, err);
                summaries.push({
                    source, status: 'error', total: 0, newlyIndexed: 0,
                    regenerated: 0, cacheHits: 0, unpublishedSkipped: 0,
                    noContentSkipped: 0, chunkWriteFailures: 0,
                    errorMessage: err.message,
                });
                setRunProgress(prev =>
                    prev.map(p =>
                        p.sourceListId === source.listId
                            ? { ...p, status: 'error', errorMessage: err.message }
                            : p,
                    ),
                );
            } finally {
                if (heartbeatTimer !== null) clearInterval(heartbeatTimer);
                try {
                    await serviceRef.current!.releaseLease(siteUrl, source);
                } catch {}
            }
        }

        const finalProgress: SourceRunProgress[] = summaries.map(s => ({
            sourceListId: s.source.listId,
            displayName: s.source.displayName ?? s.source.listId,
            status: s.status as 'completed' | 'busy' | 'error',
            total: s.total,
            processed: s.total,
            newlyIndexed: s.newlyIndexed,
            cacheHits: s.cacheHits,
            regenerated: s.regenerated,
            unpublishedSkipped: s.unpublishedSkipped,
            noContentSkipped: s.noContentSkipped,
            chunkWriteFailures: s.chunkWriteFailures,
            errorMessage: s.errorMessage,
        }));
        setRunProgress(finalProgress);
        setRunSummaries(summaries);
        _savePersistedRun(siteUrl, finalProgress, summaries);
        trackFeatureUsage(FeatureEvent.SearchIndexerRunCompleted, {
            [EventProp.SourceCount]: String(summaries.length),
            [EventProp.ErrorCount]: String(summaries.filter(s => s.status === 'error').length),
        });
        setIsRunning(false);
        isRunningRef.current = false;
        void loadSources();
    }, [siteUrl, loadSources]);

    return {
        registryExists,
        registryUrl,
        sources,
        sourcesLoading,
        workerReady,
        workerLoading,
        workerStatus,
        workerError,
        isRunning,
        runProgress,
        runSummaries,
        cacheStats,
        cacheStatsLoading,
        error,
        isProvisioning,
        provisionStatus,
        loadSources,
        provisionRegistry,
        startFullIndexRun,
        registerSource,
        deleteSource,
        clearSourceCache,
        refreshCacheStats,
        getSiteLists,
        getSourceColumns,
        updateSourceTagField,
    };
}

// ── Module-level helpers (not exported) ──────────────────────────────────────

const RUN_SUMMARY_STORAGE_PREFIX = 'searchIndexer:lastRun:';

interface PersistedRun {
    runProgress: SourceRunProgress[];
    runSummaries: IndexRunSummary[];
}

function _loadPersistedRun(siteUrl: string): PersistedRun | null {
    try {
        const raw = localStorage.getItem(`${RUN_SUMMARY_STORAGE_PREFIX}${siteUrl}`);
        return raw ? (JSON.parse(raw) as PersistedRun) : null;
    } catch {
        return null;
    }
}

function _savePersistedRun(
    siteUrl: string,
    runProgress: SourceRunProgress[],
    runSummaries: IndexRunSummary[],
): void {
    try {
        localStorage.setItem(
            `${RUN_SUMMARY_STORAGE_PREFIX}${siteUrl}`,
            JSON.stringify({ runProgress, runSummaries }),
        );
    } catch {
        // localStorage unavailable (private browsing, quota) — the summary just
        // won't survive a refresh; the run itself already completed either way.
    }
}

function _clearPersistedRun(siteUrl: string): void {
    try {
        localStorage.removeItem(`${RUN_SUMMARY_STORAGE_PREFIX}${siteUrl}`);
    } catch {
        // ignore
    }
}

function _extractItemContent(item: SourceItem, sourceType: string): string {
    if (sourceType === 'Page') {
        // CanvasContent1 is the canonical HTML canvas field from the Site Pages list item
        // (populated by _fetchAllRawListItems which does a full $expand=fields).
        // Fall back to WikiField/Body for older wiki-style pages, then description.
        const canvasContent1 = item.fields.CanvasContent1;
        if (!canvasContent1) {
            // Logged so a "no extractable content" run can be told apart from a genuine
            // Graph-fetch gap (field missing from the response entirely) vs. the field
            // being present but legitimately empty (e.g. a web-part-only page with no
            // CanvasContent1 text at all).
            console.warn('[useSearchIndexer] Page item has no CanvasContent1 — falling back', {
                title: item.title,
                itemUniqueId: item.itemUniqueId,
                fieldHasCanvasContent1Key: Object.prototype.hasOwnProperty.call(item.fields ?? {}, 'CanvasContent1'),
                hasWikiField: !!item.fields.WikiField,
                hasBody: !!item.fields.Body,
                hasDescription: !!item.fields.Description,
                availableFieldNames: Object.keys(item.fields ?? {}),
            });
        }
        return (
            canvasContent1 ||
            item.fields.WikiField ||
            item.fields.Body ||
            item.fields.Description ||
            item.title ||
            ''
        );
    }
    return (
        item.fields.Body ||
        item.fields.Body0 ||
        item.fields.Description ||
        item.fields.Title ||
        ''
    );
}

function _processItemInWorker(
    worker: Worker,
    itemUniqueId: string,
    content: string,
    title: string,
    sourceType: string,
    fileName: string,
    promotedState: number,
): Promise<SearchChunkResult[]> {
    return new Promise((resolve, reject) => {
        const handler = (e: MessageEvent) => {
            if (e.data.itemUniqueId !== itemUniqueId) return;
            worker.removeEventListener('message', handler);
            if (e.data.type === 'item-chunks') {
                resolve((e.data.chunks as SearchChunkResult[]) ?? []);
            } else if (e.data.type === 'item-error') {
                reject(new Error(e.data.error ?? 'Worker processing failed'));
            }
        };
        worker.addEventListener('message', handler);
        worker.postMessage({ type: 'process-item', itemUniqueId, content, title, sourceType, fileName, promotedState });
    });
}
